import { Button, Divider, Form, Modal, Space, Table } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import {
  DefaultRouteTabs,
  AddressModal,
  SaveButton,
} from '@/components/_common';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import {
  useEmployee,
  useEmployeeAddressCreate,
  useEmployeeAddress,
  useEmployeeAddressEdit,
  useEmployeeAddressRemove,
} from '@/core/domain/enterprise/Employees/Employees.hooks';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import { ColumnsType } from 'antd/lib/table';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { EmployeeAddress } from '@/core/domain/enterprise/Employees/Employees.types';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { useDisclosure } from '@/hooks';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { useEmployeeFormContext } from '@/components/Employee/EmployeeFormProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { PageProps } from '@/types/PageProps';
import EmployeeFooter from '@/components/Employee/EmployeeFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

const EmployeeAddressPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [formSubmit] = Form.useForm();
  const { setParamLabel } = useBreadcrumbs();
  const { employeeId, disabled } = useEmployeeFormContext();

  const [addressSelected, setAddressSelected] = useState<AddressDefaultType>();
  const [modalVisible, modalActions] = useDisclosure(false, {
    onClose: () => setAddressSelected(undefined),
  });

  const { data: employee } = useEmployee(employeeId);

  const employeeAddress = useEmployeeAddress(employeeId);
  const employeeAddressCreate = useEmployeeAddressCreate();
  const employeeAddressEdit = useEmployeeAddressEdit();
  const employeeAddressRemove = useEmployeeAddressRemove();

  useEffect(() => {
    if (employee) {
      setParamLabel(':employeeId', employee.name);
    }
  }, [employee]);

  function handleModalVisible(address?: EmployeeAddress) {
    if (address) {
      setAddressSelected({ ...address, zipCode: address.zip });
    }

    modalActions.toggle();
  }

  async function handleOnOk(address: AddressDefaultType) {
    if (employee) {
      if (addressSelected) {
        await employeeAddressEdit.mutateAsync({
          ...address,
          zip: address.zipCode,
          id: addressSelected.id,
          employeeId: employee.id,
        });
      } else {
        await employeeAddressCreate.mutateAsync({
          ...address,
          zip: address.zipCode,
          employeeId: employee.id,
        });
      }
    }
  }

  async function handleOnRemove(addressId: number) {
    Modal.confirm({
      title: intl.formatMessage({ id: 'commons.address.remove.confirm.title' }),
      content: intl.formatMessage({
        id: 'commons.address.remove.confirm.content',
      }),
      onOk: () =>
        employeeAddressRemove.mutateAsync({
          addressId,
          employeeId: Number(employeeId),
        }),
      okText: intl.formatMessage({ id: 'commons.list.action.button.remove' }),
    });
  }

  const columns: ColumnsType<EmployeeAddress> = [
    {
      dataIndex: 'zip',
      key: 'zip',
      width: COLUMNS_WIDTH.document,
      title: intl.formatMessage({ id: 'commons.address.cep' }),
      render: (value) => (value ? applyMask(value, cepMask) : '-'),
    },

    {
      dataIndex: 'street',
      key: 'street',
      ellipsis: true,
      width: 200,
      title: intl.formatMessage({ id: 'commons.address.street' }),
      render: (_, row) =>
        `${row?.street ?? ''}, ${row?.number ?? ''} - ${row?.district ?? ''}`,
    },

    {
      dataIndex: 'complement',
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
      title: intl.formatMessage({ id: 'commons.address.complement' }),
      render: (value) => value || '-',
    },

    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: COLUMNS_WIDTH.action2,
      fixed: 'right',
      align: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleModalVisible(row),
            }}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleOnRemove(row.id)}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])}
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={String(employeeId)}
      basePath={`/app/registry/employees/${employeeId}`}
      extra={
        <SpaceForButton>
          <Button
            block
            type="primary"
            onClick={() => handleModalVisible()}
            disabled={
              Number(employeeAddress.data?.data.length) > 0 ||
              !hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.entity.add' })}
          </Button>
          <SaveButton
            block
            onClick={formSubmit.submit}
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_EMPLOYEE_EDIT])
            }
          >
            Salvar
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Form form={formSubmit} name="employeeContacts" />

      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Table<EmployeeAddress>
        dataSource={employeeAddress.data?.data || []}
        rowKey={(data) => data.id}
        columns={columns}
        loading={employeeAddress.isLoading}
        scroll={{ x: getTableScrollX(columns) }}
        pagination={false}
      />

      <EmployeeFooter disabled={disabled} form={formSubmit} />

      <AddressModal
        loading={false}
        visible={modalVisible}
        onClose={handleModalVisible}
        onOk={handleOnOk}
        address={addressSelected}
        confirmLoading={
          employeeAddressCreate.isLoading || employeeAddressEdit.isLoading
        }
        simpleForm
      />
    </DefaultRouteTabs>
  );
};

export default EmployeeAddressPage;
